import { Box, Button } from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    cancelSourcingOperation,
    getCanSave,
    getIsLoading,
    saveOrderItemSourcing,
} from 'features/EditOrderItemSourcing';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { Loader } from 'shared/ui/Loader/Loader';

interface SaveCancelButtonsProps {
    onCancel?: () => void;
    onSave?: () => void;
}

export const SaveCancelButtons = memo(
    ({ onCancel, onSave }: SaveCancelButtonsProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();

        const canSave = useSelector(getCanSave);
        const isLoading = useSelector(getIsLoading);

        const handleSave = useCallback(async () => {
            if (!canSave || isLoading) return;

            await dispatch(saveOrderItemSourcing()).unwrap();
            onSave?.();
        }, [canSave, isLoading, dispatch, onSave]);

        const handleCancel = useCallback(() => {
            // Cancel any active sourcing operations
            dispatch(cancelSourcingOperation());
            onCancel?.();
        }, [dispatch, onCancel]);

        return (
            <Box display="flex" gap="12px" justifyContent="flex-end">
                <Button
                    onClick={handleCancel}
                    disabled={isLoading}
                    variant="outlined"
                    sx={{
                        minWidth: '80px',
                    }}
                >
                    {t('Cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!canSave || isLoading}
                    variant="contained"
                    startIcon={isLoading && <Loader color="inherit" />}
                    sx={{
                        minWidth: '80px',
                    }}
                >
                    {isLoading ? t('Saving...') : t('Save')}
                </Button>
            </Box>
        );
    },
);
